import { Theme } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import { Styles } from '@material-ui/styles';
import classNames from 'classnames';
import {
  addHours,
  differenceInMinutes,
  format,
  isSameDay,
  parseISO,
  setHours,
  startOfHour,
  subMinutes,
} from 'date-fns';
import isEqual from 'lodash/isEqual';
import { inject, observer } from 'mobx-react';
import type { Instance, ModelCreationType, SnapshotOut } from 'mobx-state-tree';
import React, { useState } from 'react';
import { Calendar } from 'react-big-calendar';
import type { View, ViewsProps } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import 'react-big-calendar/lib/css/react-big-calendar.css';

import AvailabilityPopup from 'src/calendars/availabilityPopup';
import { createFormats } from 'src/calendars/formats';
import { createLocalizer } from 'src/calendars/localizer';
import type { CalendarBounds, SlotInfo } from 'src/calendars/types';
import Agenda, { AgendaPropsWithStatics } from 'src/components/pages/pageElements/agenda';
import CalendarEvent from 'src/components/pages/pageElements/calendarEvent';
import { UNSCHEDULED_EVENT_STATUSES, getStartTime, getEndTime } from 'src/shared/util/events';
import CalendarStore from 'src/stores/calendar';
import { DEFAULT_VISIT_DURATION } from 'src/stores/events';
import type { EventInstance } from 'src/stores/models/event';
import type { RootStore } from 'src/stores/root';
import { colors as calendarColors, getEventStyles } from 'src/util/calendar';
import { parseUnknownDate } from 'src/util/parseUnknownDate';
import { localZone } from 'src/util/timezones';

const CALENDAR_EVENT_INCREMENT = 30;
const TIMESLOTS_PER_HOUR = 60 / CALENDAR_EVENT_INCREMENT;

/* @ts-expect-error types for react-big-calendar not maintained: https://github.com/jquense/react-big-calendar/issues/1332#issuecomment-494043469 */
const DnDCalendar = withDragAndDrop(Calendar);

// TODO: This ought to be exported by the store, but typifying it isn't simple
type CalendarStoreInstance = Instance<typeof CalendarStore>;

interface EventCalendarProps {
  calendar: CalendarStoreInstance;
  events: EventInstance[];
  onCreate: (
    options: Partial<ModelCreationType<EventInstance>>,
    wasSelecting?: boolean,
    selectPosition?: CalendarBounds,
  ) => void;
  titleBar: React.Component;
  views?: ViewsProps;
  slotPropGetter?: (rawSlotTime: Date) => {
    style?: Record<string, unknown>;
  };
  titleAccessor: (event: EventInstance) => string;
  tooltipAccessor: (event: EventInstance) => string;
  onSave: (event: EventInstance, updates: Partial<EventInstance>) => void;
  rootStore: RootStore;
  timezone?: string;
}

const EventCalendar: React.VFC<EventCalendarProps> = ({
  calendar,
  events,
  onCreate,
  titleBar,
  views = { month: true, week: true, day: true, agenda: Agenda as AgendaPropsWithStatics },
  slotPropGetter,
  titleAccessor,
  tooltipAccessor,
  onSave,
  rootStore,
  timezone,
}) => {
  const [selected, setSelected] = useState<SnapshotOut<EventInstance> | null>(null);
  const [selectedElement, setSelectedElement] = useState(null);
  const [selecting, setSelecting] = useState(false);

  const tz = timezone ?? localZone;
  const localizer = createLocalizer(tz);

  // Helper function so we can retrieve the latest mobx object for
  // the event we want to act on
  const getMobxEventById = itemId => {
    return calendar.instanceById(itemId);
  };

  const handleItemSave = updatedItem => {
    // Changes may be saved without closing the modal, so don't remove
    // the selection here
    if (selected && onSave) {
      onSave(getMobxEventById(selected.id), updatedItem);
    }
  };

  const onSelectEvent = (item, evt) => {
    setSelected(getMobxEventById(item.id));
    setSelectedElement(evt.currentTarget);
  };

  const onSelecting = () => {
    setSelecting(true);
    return true;
  };

  const onEventChange = ({ event, start, end }) => {
    const originalEvent = getMobxEventById(event.id);

    const { duration, finalStart } = calculateDurationAndStart(
      event,
      start,
      end,
      originalEvent.allDay,
    );

    const updatedEvent = {
      start: finalStart,
      duration,
    };

    if (
      !isEqual(originalEvent.start, updatedEvent.start) ||
      !isEqual(originalEvent.duration, updatedEvent.duration)
    ) {
      rootStore.events.updateEventAndSave(originalEvent, updatedEvent);
    }
  };

  const onNavigate = (date: Date) =>
    rootStore.routerStore.goTo(rootStore.routerStore.routerState.routeName, {
      params: rootStore.routerStore.routerState.params,
      queryParams: {
        view: calendar.currentView,
        date: format(date, 'yyyy-MM-dd'),
      },
    });

  const onClosePopup = async updates => {
    setSelected(null);

    if (selected && updates) {
      const originalEvent = getMobxEventById(selected.id);
      if (originalEvent) {
        rootStore.events.updateEventAndSave(originalEvent, updates);
      }
    }
  };

  const eventsToRender = () =>
    events.filter(event => !event.status || !UNSCHEDULED_EVENT_STATUSES.includes(event.status));

  const createSelectSlotHandler = () => {
    const { currentView } = calendar;
    return (select: SlotInfo) => {
      const options: { start?: Date; duration?: number } = {};
      if (select.start) {
        let start = parseUnknownDate(select.start);
        if (currentView === 'month') {
          // Clicking on a day in the month view gives a start time of 00:00:00,
          // so instead we'll initialize to the next hour if today was clicked,
          // otherwise to 8am
          if (isSameDay(start, new Date())) {
            start = startOfHour(addHours(new Date(), 1));
          } else {
            start = startOfHour(setHours(start, 8));
          }
        } else if (select.action === 'click') {
          options.duration = DEFAULT_VISIT_DURATION;
        } else if (select.end) {
          const duration = differenceInMinutes(parseUnknownDate(select.end), start);
          if (duration > 0) {
            options.duration = duration;
          }
        }
        options.start = start;
      }
      const wasSelecting = selecting;
      setSelecting(false);
      return onCreate(options, wasSelecting, select.bounds);
    };
  };

  // TODO - Calendar child components aren't mobx observers so don't get re-rendered unless
  // we explicitly dereference all the fields we care about to set up the watchers. I'm sure
  // there's a more graceful way, like using accessors that contain observers...
  events.forEach(i => [i.title, i.allDay, i.duration, i.start, i.status]);

  const classes = ['borderless-calendar', 'darkened-calendar'];

  // TODO: This could be more clever but not calculating this now.
  const defaultTimeToScrollTo = setHours(calendar.currentDate, 8);

  return (
    <div>
      {titleBar}
      <DnDCalendar
        className={classNames(classes)}
        date={calendar.currentDate}
        view={calendar.currentView as View}
        scrollToTime={defaultTimeToScrollTo}
        slotPropGetter={slotPropGetter}
        titleAccessor={titleAccessor}
        views={views}
        localizer={localizer}
        events={eventsToRender()}
        onEventDrop={onEventChange}
        onEventResize={onEventChange}
        selectable
        onSelecting={onSelecting}
        onSelectEvent={onSelectEvent}
        onSelectSlot={createSelectSlotHandler()}
        onView={() => {}}
        onNavigate={onNavigate}
        step={CALENDAR_EVENT_INCREMENT}
        timeslots={TIMESLOTS_PER_HOUR}
        style={{
          // 250px is an approximation for the elements above the calendar in the viewport,
          // TODO: Flexbox is supposed to handle this
          height: 'calc(100vh - 250px)',
        }}
        tooltipAccessor={tooltipAccessor}
        startAccessor={getStartTime}
        endAccessor={getEndTime}
        formats={createFormats(tz)}
        components={{
          event: eventProps => <CalendarEvent currentView={calendar.currentView} {...eventProps} />,
        }}
        eventPropGetter={getEventStyles(calendar.currentView)}
      />
      {!!selected && (
        <AvailabilityPopup
          calendar={calendar}
          item={selected}
          itemElement={selectedElement}
          onClose={onClosePopup}
          onSave={handleItemSave}
        />
      )}
    </div>
  );
};

/**
 * Prevent an event's duration from becoming 0 or negative. Dragging an event up from the bottom resize lines can cause this.
 * @param event Updated event object.
 * @param initialStart Event start value determined by the calendar.
 * @param end Event end value determined by the calendar.
 * @param allDay Boolean that describes whether an event is an all day event.
 */
export function calculateDurationAndStart(
  event: EventInstance,
  start: string | Date,
  end?: string | Date,
  allDay?: boolean,
) {
  // If it's an all day event, we want to set the start time to the beginning of the day.
  let parsedStart = parseUnknownDate(start);
  if (allDay) {
    parsedStart = parseISO(`${format(parsedStart, 'yyyy-MM-dd')}T00:00:00.000Z`);
  }

  let duration = (event.duration ?? 0) <= 0 ? CALENDAR_EVENT_INCREMENT : event.duration;

  if (end) {
    const parsedEnd = parseUnknownDate(end);
    if (isEqual(parsedStart, parsedEnd)) {
      parsedStart = subMinutes(parsedStart, CALENDAR_EVENT_INCREMENT);
    } else {
      duration = differenceInMinutes(parsedEnd, parsedStart);
    }
  }

  return { duration, finalStart: parsedStart };
}

const styles: Styles<Theme, any> = () => ({
  '@global': {
    '.borderless-calendar': {
      '& .rbc-time-view': {
        border: 'none',
      },
      '& .rbc-timeslot-group': {
        borderBottom: 'none',
        minHeight: 80,
      },
      '& .rbc-header + .rbc-header, .rbc-time-header-content, .rbc-time-content > * + * > *, .rbc-time-header-content .rbc-day-bg + .rbc-day-bg':
        {
          borderLeft: 'none',
        },
      '& .rbc-time-header-content .rbc-header': {
        textAlign: 'left',
        borderBottom: 'none',
      },
      '& .rbc-time-slot, .rbc-time-header.rbc-overflowing': {
        borderRight: 'none',
      },
      '& .rbc-time-column': {
        marginRight: 5,
      },
      '& .rbc-time-content': {
        padding: 5,
      },
      '& .rbc-day-slot .rbc-timeslot-group': {
        borderTop: `1px solid ${calendarColors.OFF_WHITE}`,
      },
      '& .rbc-event': {
        border: '1px solid',
      },
    },
    '.striped-calendar': {
      '& .rbc-day-slot': {
        background: `repeating-linear-gradient( -45deg, ${calendarColors.OFF_WHITE}, ${calendarColors.OFF_WHITE} 12px, ${calendarColors.LIGHT_GREY} 12px, ${calendarColors.LIGHT_GREY} 28px );`,
      },
    },
    '.darkened-calendar': {
      '& .rbc-day-slot .rbc-time-slot': {
        backgroundColor: calendarColors.MEDIUM_GREY,
        borderTop: 'none',
      },
    },

    // Override default calendar styles
    '.rbc-label': {
      fontSize: 12,
    },
    '.rbc-header': {
      fontWeight: 'normal',
    },
    '.rbc-today': {
      '&.rbc-now, &.rbc-header, &.rbc-day-bg': {
        backgroundColor: 'transparent',
      },
      '&.rbc-header': {
        fontWeight: 'bold',
      },
    },
    // This allows us to see the hover styling when
    // hovering over half hour blocks in the weekly/daily views
    '.rbc-events-container': {
      pointerEvents: 'none',
    },
    // This allows events on the weekly/daily views to still
    // get the expected hover styling/pointer behavior
    '.rbc-event': {
      pointerEvents: 'all',
    },
    '.rbc-agenda-view': {
      '& .rbc-agenda-content': {
        '& .rbc-agenda-event-cell': {
          padding: 0,
        },
      },
    },
  },
  agendaButton: {
    textTransform: 'none',
    fontSize: 16,
    lineHeight: 'normal',
    width: '100%',
    justifyContent: 'flex-start',
    paddingTop: 5,
    borderRadius: 0,
  },
});

export default withStyles(styles)(inject('rootStore')(observer(EventCalendar)));
